import { Link } from 'react-router-dom';
import { CheckCircle2, Printer, Home, CalendarDays, MapPin, Clock } from 'lucide-react';
import { formatFairDate, venueLine } from './fairApi';

export const RegistrationSuccess = ({ fair, result, kind }) => {
  const isEmp = kind === 'employer';
  return (
    <div className="max-w-2xl mx-auto px-4 py-12 sm:py-20">
      <div data-testid={`${kind}-registration-success-card`} className="jf-rise bg-white rounded-[2rem] border border-slate-200 shadow-2xl shadow-orange-900/10 overflow-hidden">
        <div className="bg-gradient-to-br from-[var(--jf-primary)] to-amber-500 px-8 py-10 text-center text-white">
          <CheckCircle2 className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-2xl sm:text-3xl font-black">{isEmp ? 'Employer Registration Submitted' : 'Registration Successful!'}</h1>
          <p className="mt-2 text-white/90">{fair.name}</p>
        </div>
        <div className="p-8 text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-slate-500">{isEmp ? 'Employer Registration ID' : 'Your Candidate Registration ID'}</p>
          <p data-testid={`${kind}-registration-id-display`}
            className="font-mono text-3xl sm:text-4xl font-bold text-[var(--jf-primary)] bg-orange-50 px-6 py-4 rounded-2xl border border-orange-200 inline-block my-4 tracking-wider">
            {result.registration_id}
          </p>
          <p className="text-slate-700 font-semibold">{isEmp ? result.company_name : result.full_name}</p>
          {isEmp ? (
            <>
              <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-800 px-4 py-1.5 text-sm font-bold" data-testid="employer-registration-status">
                <Clock className="w-4 h-4" /> Status: Under Verification
              </p>
              <p className="mt-4 text-slate-500 text-sm">Your registration will be reviewed by the VVVSI Employer Verification Team. Please keep your Employer Registration ID for future communication.</p>
            </>
          ) : (
            <p className="mt-4 text-slate-500 text-sm">Please save this ID and bring it with you to the Job Fair.</p>
          )}
          <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm text-left">
            {formatFairDate(fair) && <p className="flex items-center gap-2 rounded-xl bg-slate-50 p-3"><CalendarDays className="w-4 h-4 text-[var(--jf-primary)]" />{formatFairDate(fair)}</p>}
            {venueLine(fair) && <p className="flex items-center gap-2 rounded-xl bg-slate-50 p-3"><MapPin className="w-4 h-4 text-[var(--jf-primary)]" />{venueLine(fair)}</p>}
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-3 jf-noprint">
            <button type="button" onClick={() => window.print()} data-testid={`${kind}-registration-print-button`}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--jf-navy)] text-white px-6 py-3 font-semibold hover:bg-slate-800 transition-colors">
              <Printer className="w-4 h-4" /> Download / Save Registration
            </button>
            <Link to={`/jobfairs/${fair.slug}`} data-testid={`${kind}-registration-home-link`}
              className="inline-flex items-center gap-2 rounded-full border-2 border-slate-200 px-6 py-3 font-semibold text-slate-700 hover:border-[var(--jf-primary)] transition-colors">
              <Home className="w-4 h-4" /> Back to Job Fair
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RegistrationClosed = ({ fair, kind }) => (
  <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid={`${kind}-registration-closed`}>
    <p className="jf-heading text-3xl font-black text-[var(--jf-navy)]">Registrations are closed</p>
    <p className="mt-3 text-slate-500">{fair.effective_status === 'completed' ? 'This job fair has been completed.' : 'Registration is not open at the moment.'}</p>
    <Link to={`/jobfairs/${fair.slug}`} className="mt-6 inline-flex rounded-full bg-[var(--jf-primary)] text-white px-6 py-3 font-semibold">Back to Job Fair</Link>
  </div>
);

export const FormHero = ({ fair, title, subtitle }) => (
  <div className="relative overflow-hidden jf-grain bg-gradient-to-b from-[#FFF1E8] to-[#FFFBF7] border-b border-orange-100">
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[var(--jf-primary)]">{fair.name}</p>
      <h1 className="mt-2 text-3xl sm:text-5xl font-black tracking-tight text-[var(--jf-navy)]">{title}</h1>
      <p className="mt-3 text-slate-600">{[formatFairDate(fair), venueLine(fair)].filter(Boolean).join(' | ')}</p>
      {subtitle && <p className="mt-2 text-slate-500">{subtitle}</p>}
      <p className="mt-4 inline-flex rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-sm font-bold">Registration is Free</p>
    </div>
  </div>
);
